import { useSubjectUpdate } from '@healthblocks-io/core'
import { t } from '@lingui/macro'
import { Formik } from 'formik'
import { useState } from 'react'
import { useTheme } from 'styled-components'

import { Checkbox } from '@/@bothrs-ui/web/components/forms/checkbox/Checkbox'
import { FlexColumn, Spacing } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { SafeArea } from '@/@bothrs-ui/web/components/safe-area'
import { P } from '@/@bothrs-ui/web/components/typography/Body'
import { H1 } from '@/@bothrs-ui/web/components/typography/Heading'
import { CenterLayout } from '@/layout/components/center-layout'
import { ExternalLink } from '@/modules/shared/components/external-link/ExternalLink'
import { useTranslated } from '@/utils/translation'
import { PrivacyPolicyContentBox } from './PrivacyPolicyPage.styled'

const initialvalues: { privacy: string[] } = { privacy: [] }

const PrivacyPolicyPage = () => {
  const theme = useTheme()
  const [{ mutate }, updateSubject] = useSubjectUpdate()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const title = useTranslated('privacy-policy.title')
  const body = useTranslated('privacy-policy.body')
  const url = useTranslated('privacy-policy.url')

  const bodyParagraphs = body.split('\n').filter((paragraph) => paragraph.trim())

  // Interaction methods
  // ------------------------------------------------------------------------- /
  const handleSubmit = () => {
    // setLocalPrivacyConsent(true)
    // navigate('/auth/login')

    // Update the member with its updated terms and conditions policy
    setIsLoading(true)

    // Refresh the user before updating the actor to make sure we have the latest metadata
    mutate()
      .then((latestActor) =>
        updateSubject({
          meta: {
            ...latestActor?.meta,
            hasAcceptedPrivacyPolicy: {
              value: true,
              date: new Date().toJSON(),
            },
          },
        }),
      )
      .finally(() => {
        mutate()
        setIsLoading(false)
      })
  }

  return (
    <SafeArea>
      <CenterLayout backgroundColor={theme.Colors.Background.Light}>
        <Formik initialValues={initialvalues} onSubmit={handleSubmit}>
          {({ values, submitForm }) => {
            return (
              <FlexColumn alignItems='center'>
                <PrivacyPolicyContentBox>
                  <H1>{title}</H1>
                  {bodyParagraphs.map((paragraph, index) => (
                    <P key={index} type='BodyLargeRegular'>
                      {paragraph}
                    </P>
                  ))}
                  {url && (
                    <ExternalLink href={url} icon={['fal', 'external-link']} target='_blank' rel='noreferrer'>
                      {t({
                        id: 'onboarding.privacy.link-title',
                        comment: 'Full privacy statement',
                        message: 'Volledige privacyverklaring',
                      })}
                    </ExternalLink>
                  )}
                </PrivacyPolicyContentBox>

                <Spacing height={theme.UI.Spacing.Space8} />

                <Checkbox
                  type='checkbox'
                  name='privacy'
                  id='checked'
                  value='checked'
                  label={t({
                    id: 'onboarding.privacy.accept',
                    comment: 'I have taken note of the privacy statement',
                    message: 'Ik heb kennis genomen van de privacyverklaring.',
                  })}
                />

                <Spacing height={theme.UI.Spacing.Space8} />

                <Button
                  variant='Primary'
                  type='submit'
                  inline
                  isLoading={isLoading}
                  disabled={!values.privacy.includes('checked')}
                  onClick={submitForm}
                >
                  {t({
                    id: 'globals.continue',
                    comment: 'Global for continue',
                    message: 'Ga verder',
                  })}
                </Button>
              </FlexColumn>
            )
          }}
        </Formik>
      </CenterLayout>
    </SafeArea>
  )
}

export { PrivacyPolicyPage }
